import React, { useEffect } from 'react';
import { Spinner } from '@tink/core/src/components/atoms/Spinner/Spinner';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { useIntl } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { PrimaryContentColumn } from '../../../components/atoms/ContentBody/ContentBody';
import { validateQueryParameter } from '../../../utilities/urlParametersValidator';
import { ContentOverlay } from '../../../components/molecules/ContentOverlay/ContentOverlay';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { showLoadingMessages } from '../../../models/configuration';
import { CircularLogo } from '../../../components/Templates/TemplateColor.styles';
import { styled } from '../../theme/styled';
import { OverlayContainer } from './molecules/OverlayContainer/OverlayContainer';
import { parentLoadingMessenger } from './organisms/ParentLoadingMessenger/ParentLoadingMessenger';
import { messages } from './messages';

export type LoadingOverlayProps = {
  text?: JSX.Element;
  ariaLabel?: string;
  iconUrl?: string;
};

const spinnerDiameter = 80; // Pixels
const iconDiameter = 60; // Pixels

export const IconOverlay = styled.div`
  height: ${spinnerDiameter}px;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ text, ariaLabel, iconUrl }) => {
  const { data } = useConfiguration();
  const showMessage = showLoadingMessages(data);
  const loadingMessage = showMessage ? text : undefined;
  const isIframe = Boolean(validateQueryParameter(TinkLinkUrlParameter.IFRAME, /^true$/));
  const { formatMessage } = useIntl();

  useEffect(() => {
    const isTinkLinkEmbedded = window.top !== window;
    const shouldPostLoadingStatus = isTinkLinkEmbedded && isIframe;

    if (shouldPostLoadingStatus) {
      parentLoadingMessenger.execute(true);
    }

    return () => {
      if (shouldPostLoadingStatus) {
        parentLoadingMessenger.execute(false);
      }
    };
  });

  const spinnerAriaLabel = ariaLabel ?? formatMessage(messages.label);

  return (
    <ContentOverlay>
      <PrimaryContentColumn centerVertically>
        <OverlayContainer
          text={loadingMessage}
          figure={
            <Box mx="normedium" my="normedium" data-testid="loading-overlay" relative>
              {iconUrl ? (
                <>
                  <Spinner aria-label={spinnerAriaLabel} size={spinnerDiameter} thickness={1} />
                  <IconOverlay>
                    <CircularLogo src={iconUrl} diameter={iconDiameter} />
                  </IconOverlay>
                </>
              ) : (
                <Spinner aria-label={spinnerAriaLabel} />
              )}
            </Box>
          }
        />
      </PrimaryContentColumn>
    </ContentOverlay>
  );
};
